import type React from "react";
import { forwardRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type {
  IStreamData,
  IThumbnailTemplate,
} from "../../../../../types/thumbnail-template";
import { tokens } from "../../../../../theme";

import muzukuru from "../../../../../assets/muzukuru.png";
import muzukuruBlack from "../../../../../assets/muzukurublack.png";
import oldMutuallogo from "../../../../../assets/old-mutual.png";
import firstmutuallogo from "../assets/firstmutual.png";
import {
  formatDateToLongString,
  formatLifespan,
  formatTime,
} from "../../../../../utils/FormatDate";

interface FuneralTemplateProps {
  streamData: IStreamData;
  template: IThumbnailTemplate;
  style?: React.CSSProperties;
}

const FuneralTemplate = forwardRef<HTMLDivElement, FuneralTemplateProps>(
  ({ streamData, template, style }, ref) => {
    const deceasedName =
      streamData?.bookingData?.booking_type === "FUNERAL" ||
      streamData?.bookingData?.booking_type === "FUNERAL_HOME"
        ? `${streamData?.bookingData?.deceased_name || ""} ${
            streamData?.bookingData?.deceased_second_name || ""
          } ${streamData?.bookingData?.deceased_surname || ""}`.trim()
        : `${streamData?.booking?.deceased?.[0]?.first_name || ""} ${
            streamData?.booking?.deceased?.[0]?.second_name || ""
          } ${streamData?.booking?.deceased?.[0]?.surname || ""}`.trim();

    const formattedDate = formatDateToLongString(streamData?.date);
    const formattedTime = formatTime(streamData?.shoot_from || "");
    const location = streamData?.location || streamData.service?.venue?.name;
    const address = `${streamData.address || ""}`;
    const lifespan = formatLifespan(
      streamData?.bookingData?.deceased_dob,
      streamData?.bookingData?.deceased_dod,
    );

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const textColor = template?.them_color || "#ffffff";
    const logoSrc = textColor === "#000000" ? muzukuruBlack : muzukuru;

    const isOldMutual = /old mutual/i.test(streamData?.location ?? "");
    const isFirstMutual = /first mutual/i.test(streamData?.location ?? "");

    const person_image =
      streamData?.booking?.deceased?.[0]?.person_image ||
      streamData?.bookingData?.deceased_photo_url ||
      streamData?.bookingData?.deceased_picture_url ||
      "";

    const showAvatar = !!person_image;

    return (
      <Box
        ref={ref}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "stretch",
          boxSizing: "border-box",
          backgroundImage: `url(${template.background_image})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          color: textColor,
          fontFamily: '"Montserrat", sans-serif',
          "& *": {
            fontFamily: '"Montserrat", sans-serif',
            boxSizing: "border-box",
          },
          ...style,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            px: "56px",
            py: "42px",
            gap: "28px",
          }}
        >
          {/* LEFT */}
          <Box
            sx={{
              flex: "0 0 46%",
              minWidth: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "500px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: "16px",
              }}
            >
              {showAvatar ? (
                <Box
                  sx={{
                    width: "360px",
                    height: "360px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: `4px solid ${textColor}`,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={person_image}
                    alt="Deceased"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = "";
                    }}
                  />
                </Box>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: "40px",
                      fontWeight: 600,
                      lineHeight: 1.15,
                      textAlign: "center",
                    }}
                  >
                    In Loving Memory Of
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "48px",
                      fontWeight: 700,
                      lineHeight: 1.08,
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {deceasedName}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "30px",
                      fontStyle: "italic",
                      lineHeight: 1.2,
                      textAlign: "center",
                    }}
                  >
                    {lifespan}
                  </Typography>
                </>
              )}

              {isOldMutual || isFirstMutual ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "24px",
                      textTransform: "uppercase",
                      mt: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    Service provided by :
                  </Typography>

                  <img
                    src={isOldMutual ? oldMutuallogo : firstmutuallogo}
                    alt={isOldMutual ? "Old Mutual Logo" : "First Mutual Logo"}
                    style={{
                      width: "300px",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  />
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        maxWidth: "110px",
                        height: "2px",
                        backgroundColor: textColor,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        lineHeight: 1.1,
                      }}
                    >
                      Broadcast by
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        maxWidth: "110px",
                        height: "2px",
                        backgroundColor: textColor,
                      }}
                    />
                  </Box>

                  <img
                    src={logoSrc}
                    alt="Logo"
                    style={{
                      width: "300px",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  />
                </>
              )}
            </Box>
          </Box>

          {/* RIGHT */}
          <Box
            sx={{
              flex: "0 0 54%",
              minWidth: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "620px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: "14px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "28px",
                  letterSpacing: "0.05em",
                  fontStyle: "italic",
                  fontWeight: 700,
                  lineHeight: 1.15,
                }}
              >
                In Loving Memory of
              </Typography>

              <Typography
                sx={{
                  width: "100%",
                  fontSize: "58px",
                  fontWeight: 700,
                  lineHeight: 1.05,
                  textAlign: "center",
                  wordBreak: "break-word",
                }}
              >
                {deceasedName}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  my: 1,
                }}
              >
                <Typography
                  sx={{
                    backgroundColor: colors.neutral[50],
                    color: colors.neutral[700],
                    borderTopRightRadius: "10px",
                    borderBottomLeftRadius: "10px",
                    fontSize: "34px",
                    fontWeight: 700,
                    px: "10px",
                    py: "2px",
                    lineHeight: 1.1,
                  }}
                >
                  LIVE
                </Typography>

                <Typography
                  sx={{
                    fontSize: "34px",
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  STREAM
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "42px",
                  fontWeight: 500,
                  lineHeight: 1.15,
                }}
              >
                {formattedDate}
              </Typography>

              <Typography
                sx={{
                  fontSize: "42px",
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {formattedTime}
              </Typography>

              <Typography
                sx={{
                  fontSize: "23px",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                  mt: 1,
                }}
              >
                Broadcasting live from
              </Typography>

              <Typography
                sx={{
                  fontSize: "34px",
                  fontWeight: 500,
                  lineHeight: 1.15,
                  textAlign: "center",
                  wordBreak: "break-word",
                }}
              >
                {location}
                {address ? (
                  <>
                    <br />
                    {address}
                  </>
                ) : null}
              </Typography>

              {(isOldMutual || isFirstMutual) && (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        height: "2px",
                        backgroundColor: textColor,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "22px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        lineHeight: 1.1,
                      }}
                    >
                      Broadcast by
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        height: "2px",
                        backgroundColor: textColor,
                      }}
                    />
                  </Box>

                  <img
                    src={logoSrc}
                    alt="Logo"
                    style={{
                      width: "280px",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

FuneralTemplate.displayName = "FuneralTemplate";
export default FuneralTemplate;
